import type { HTMLAttributes, ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Remove the default padding — useful when the card wraps its own layout. */
  flush?: boolean;
  /** Tight padding for compact cards (e.g., small stat cards). */
  tight?: boolean;
  /** Opt into the subtle background gradient used for emphasis (e.g., hero). */
  emphasis?: boolean;
}

export function Card({ children, flush, tight, emphasis, className = '', ...rest }: CardProps) {
  const pad = flush ? '' : tight ? 'p-3' : 'p-4';
  return (
    <div
      {...rest}
      className={`rounded-2xl bg-white shadow-card border border-ink-100 dark:bg-ink-800 dark:border-ink-700 ${pad} ${emphasis ? 'relative overflow-hidden' : ''} ${className}`}
    >
      {emphasis && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent-50 to-transparent dark:from-ink-700 dark:to-transparent" />
      )}
      {emphasis ? <div className="relative">{children}</div> : children}
    </div>
  );
}

interface SectionHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  /** Use as a card's top bar — adds a bottom border. */
  bordered?: boolean;
  /** Use the subtle surface color. */
  muted?: boolean;
}

export function SectionHeader({ title, subtitle, right, bordered, muted }: SectionHeaderProps) {
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-2 ${bordered ? 'border-b border-ink-100 dark:border-ink-700' : ''} ${muted ? 'bg-ink-50/60 dark:bg-ink-800/50' : ''}`}
    >
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-[11px] text-ink-500 dark:text-ink-300">{subtitle}</div>
        )}
      </div>
      {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
    </div>
  );
}
